import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CohortMemberProgressResponse } from '../../../api/Api';
import { useApiClient } from '../../../api/ApiClientContext';
import { useConflictSafeMutation } from '../../../hooks/useConflictSafeMutation';
import { EndorsementModal } from '../TeamWidget/EndorsementModal';
import styles from './EndorseCard.module.scss';

interface EndorseCardProps {
  className?: string;
}

export const EndorseCard: React.FC<EndorseCardProps> = ({ className = '' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<CohortMemberProgressResponse | null>(null);
  const [endorsedUserIds, setEndorsedUserIds] = useState<Set<string>>(new Set());
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  // Fetch cohort members
  const { data: cohortMembers } = useQuery({
    queryKey: ['cohortMembers'],
    queryFn: async () => {
      const res = await apiClient.api.getCohortMembersApiV1SocialCohortMembersGet();
      return res?.data;
    },
  });

  // Fetch current user to exclude from suggestions
  const { data: currentUser } = useQuery({
    queryKey: ['currentUserProfile'],
    queryFn: async () => {
      const res = await apiClient.api.getCurrentUserProfileApiV1AuthMeGet();
      return res?.data;
    },
  });

  // Endorsement mutation
  const { mutate: createEndorsement } = useConflictSafeMutation({
    mutationFn: async (data: { endorsed_user_id: string; habit_id: string; endorsement_option: string }) => {
      const res = await apiClient.api.createEndorsementApiV1SocialEndorsementsPost(data);
      return res?.data;
    },
    onSuccess: (_data, variables) => {
      setEndorsedUserIds(prev => new Set(prev).add(variables.endorsed_user_id));
      setIsModalOpen(false);
      setSelectedMember(null);
      queryClient.invalidateQueries({ queryKey: ['cohortMembers'] });
    },
  });

  // Filter eligible members (not current user, has active habits, not yet endorsed today)
  const eligibleMembers = useMemo(() => {
    if (!cohortMembers || !currentUser?.user) return [];
    
    return cohortMembers.filter(
      (member) => 
        member.user.id !== currentUser.user.id && 
        member.active_habits.length > 0 &&
        !member.user.endorsed_any_habit_today &&
        !endorsedUserIds.has(member.user.id)
    );
  }, [cohortMembers, currentUser, endorsedUserIds]);

  const handleEndorseClick = (member: CohortMemberProgressResponse) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  // Get initials for avatar
  const getInitials = (member: CohortMemberProgressResponse) => {
    const firstName = (member.user as any)?.first_name || '';
    const lastName = (member.user as any)?.last_name || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || 
           member.user?.full_name?.charAt(0)?.toUpperCase() || '?';
  };

  const taskCount = eligibleMembers.length;

  return (
    <>
      <div className={`${styles.endorseCard} ${className}`}>
        <div className={styles.endorseCard__header}>
          <div className={styles.endorseCard__titleRow}>
            <h3 className={styles.endorseCard__title}>Your tasks</h3>
            {taskCount > 0 && (
              <span className={styles.endorseCard__badge}>{taskCount}</span>
            )}
          </div>
          <button className={styles.endorseCard__viewMore}>View more</button>
        </div>

        <div className={styles.endorseCard__content}>
          <h4 className={styles.endorseCard__subtitle}>Endorse your teammates</h4>
          
          {eligibleMembers.length === 0 ? (
            <p className={styles.endorseCard__empty}>No teammates to endorse right now</p>
          ) : (
            <div className={styles.endorseCard__membersList}>
              {eligibleMembers.slice(0, 3).map((member) => {
                const colors = ['#4C6EF5', '#FA5252', '#12B886', '#FAB005', '#868E96'];
                const colorIndex = member.user.id.charCodeAt(0) % colors.length;
                
                return (
                  <div key={member.user.id} className={styles.endorseCard__memberRow}>
                    <div 
                      className={styles.endorseCard__avatar}
                      style={{ backgroundColor: colors[colorIndex] }}
                    >
                      {member.user.avatar_url ? (
                        <img src={member.user.avatar_url} alt={member.user.full_name} />
                      ) : (
                        <span>{getInitials(member)}</span>
                      )}
                    </div>
                    <span className={styles.endorseCard__memberName}>
                      {member.user.full_name}
                    </span>
                    <button
                      className={styles.endorseCard__endorseBtn}
                      onClick={() => handleEndorseClick(member)}
                    >
                      Endorse
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {eligibleMembers.length > 0 && (
            <button className={styles.endorseCard__viewAllBtn}>
              View all endorsements
            </button>
          )}
        </div>
      </div>

      {selectedMember && (
        <EndorsementModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          member={selectedMember}
          mutate={createEndorsement}
        />
      )}
    </>
  );
};

export default EndorseCard;
